import { RENDER_STATE_VERSION } from '../engine/rendering/RenderStateAdapter.js';

export const CANVAS_WORLD_RENDERER_VERSION = 1;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const VOLUME_PALETTE = Object.freeze({
  solid: { fill: 'rgba(54, 45, 34, 0.82)', stroke: 'rgba(176, 143, 93, 0.9)' },
  water: { fill: 'rgba(27, 93, 105, 0.72)', stroke: 'rgba(95, 198, 214, 0.95)' },
  climbable: { fill: 'rgba(84, 70, 46, 0.72)', stroke: 'rgba(195, 164, 105, 0.95)' },
  'crawl-under': { fill: 'rgba(36, 53, 39, 0.78)', stroke: 'rgba(119, 181, 126, 0.95)' },
  cover: { fill: 'rgba(34, 92, 50, 0.58)', stroke: 'rgba(96, 196, 112, 0.9)' },
  hazard: { fill: 'rgba(112, 145, 29, 0.72)', stroke: 'rgba(206, 255, 72, 0.95)' },
});

const WEATHER_PALETTE = Object.freeze({
  clear: { top: '#152f27', bottom: '#07100d', mist: 'rgba(177, 214, 190, 0.035)' },
  rain: { top: '#13242a', bottom: '#050b0d', mist: 'rgba(164, 201, 209, 0.065)' },
  fog: { top: '#37483f', bottom: '#111b17', mist: 'rgba(232, 244, 237, 0.11)' },
  storm: { top: '#172029', bottom: '#030608', mist: 'rgba(188, 206, 219, 0.07)' },
});

function assertMount(value) {
  if (!value || typeof value.append !== 'function' || typeof value.replaceChildren !== 'function') {
    throw new TypeError('mount must be a DOM element');
  }
  return value;
}

function assertPlainObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  return value;
}

function finite(value, fallback = 0) {
  return Number.isFinite(value) ? value : fallback;
}

function createAmbientPoints(count = 160) {
  return Array.from({ length: count }, (_, index) => {
    const angle = index * 2.399963229728653;
    const radius = 2.5 + ((index * 37) % 90) * 0.22;
    return Object.freeze({
      x: Math.cos(angle) * radius,
      z: Math.sin(angle) * radius,
      height: 0.05 + ((index * 11) % 12) * 0.015,
      width: 0.015 + ((index * 7) % 6) * 0.004,
    });
  });
}

function normalizeRendererDiagnostics(value, previous) {
  assertPlainObject(value, 'diagnostics');
  return Object.freeze({
    showGrid: value.showGrid === undefined ? previous.showGrid : Boolean(value.showGrid),
    showTraversal: value.showTraversal === undefined
      ? previous.showTraversal
      : Boolean(value.showTraversal),
    showLabels: value.showLabels === undefined ? previous.showLabels : Boolean(value.showLabels),
    showBounds: value.showBounds === undefined ? previous.showBounds : Boolean(value.showBounds),
  });
}

export class CanvasWorldRenderer {
  constructor({
    mount,
    windowRef = globalThis.window,
    documentRef = globalThis.document,
    maxDevicePixelRatio = 2,
  }) {
    this.mount = assertMount(mount);
    if (!documentRef?.createElement) throw new TypeError('documentRef must provide createElement()');
    if (!Number.isFinite(maxDevicePixelRatio) || maxDevicePixelRatio < 1) {
      throw new RangeError('maxDevicePixelRatio must be at least 1');
    }
    this.windowRef = windowRef;
    this.documentRef = documentRef;
    this.maxDevicePixelRatio = maxDevicePixelRatio;
    this.canvas = null;
    this.context = null;
    this.overlay = null;
    this.resizeObserver = null;
    this.started = false;
    this.visible = false;
    this.frameCount = 0;
    this.lastTick = null;
    this.lastChecksum = null;
    this.lastRenderMilliseconds = 0;
    this.lastError = null;
    this.width = 0;
    this.height = 0;
    this.devicePixelRatio = 1;
    this.lastCounts = { wildlife: 0, volumes: 0, actors: 0 };
    this.diagnostics = Object.freeze({
      showGrid: true,
      showTraversal: false,
      showLabels: false,
      showBounds: false,
    });
    this.ambientPoints = createAmbientPoints();
    this.onResize = () => this.resize();
  }

  start({ visible = true } = {}) {
    if (typeof visible !== 'boolean') throw new TypeError('visible must be a boolean');
    if (!this.started) {
      this.canvas = this.documentRef.createElement('canvas');
      this.canvas.className = 'engine2-canvas';
      this.canvas.tabIndex = 0;
      this.canvas.setAttribute('aria-label', 'Engine 2.0 world renderer');
      this.canvas.dataset.rendererVersion = String(CANVAS_WORLD_RENDERER_VERSION);
      this.context = this.canvas.getContext?.('2d', { alpha: false }) ?? null;
      if (!this.context) throw new Error('Canvas 2D rendering is unavailable');

      this.overlay = this.documentRef.createElement('div');
      this.overlay.className = 'engine2-renderer__status';
      this.overlay.setAttribute('aria-live', 'polite');
      this.mount.replaceChildren(this.canvas, this.overlay);

      if (typeof this.windowRef?.ResizeObserver === 'function') {
        this.resizeObserver = new this.windowRef.ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.mount);
      } else {
        this.windowRef?.addEventListener?.('resize', this.onResize);
      }
      this.started = true;
    }
    this.setVisible(visible);
    this.resize();
    return this.getStatus();
  }

  stop({ clear = true } = {}) {
    if (typeof clear !== 'boolean') throw new TypeError('clear must be a boolean');
    this.visible = false;
    if (this.canvas) this.canvas.hidden = true;
    if (this.overlay) this.overlay.hidden = true;
    if (clear && this.context && this.width > 0 && this.height > 0) {
      this.context.setTransform?.(1, 0, 0, 1, 0, 0);
      this.context.clearRect?.(0, 0, this.canvas.width, this.canvas.height);
    }
    return this.getStatus();
  }

  destroy() {
    this.resizeObserver?.disconnect?.();
    this.resizeObserver = null;
    this.windowRef?.removeEventListener?.('resize', this.onResize);
    this.canvas?.remove?.();
    this.overlay?.remove?.();
    this.canvas = null;
    this.context = null;
    this.overlay = null;
    this.started = false;
    this.visible = false;
    this.width = 0;
    this.height = 0;
    return this.getStatus();
  }

  setVisible(visible) {
    if (typeof visible !== 'boolean') throw new TypeError('visible must be a boolean');
    this.visible = visible;
    if (this.canvas) this.canvas.hidden = !visible;
    if (this.overlay) this.overlay.hidden = !visible;
    this.mount.dataset.renderingVisible = String(visible);
    if (visible) this.focus();
    return this.getStatus();
  }

  focus() {
    this.canvas?.focus?.({ preventScroll: true });
  }

  setDiagnostics(options = {}) {
    this.diagnostics = normalizeRendererDiagnostics(options, this.diagnostics);
    return this.getStatus();
  }

  resize() {
    if (!this.started || !this.canvas || !this.context) return this.getStatus();
    const rect = this.mount.getBoundingClientRect?.() ?? {};
    const width = Math.max(1, Math.round(
      finite(rect.width, 0)
      || finite(this.mount.clientWidth, 0)
      || finite(this.windowRef?.innerWidth, 0)
      || 640,
    ));
    const height = Math.max(1, Math.round(
      finite(rect.height, 0)
      || finite(this.mount.clientHeight, 0)
      || finite(this.windowRef?.innerHeight, 0)
      || 360,
    ));
    const devicePixelRatio = clamp(
      finite(this.windowRef?.devicePixelRatio, 1),
      1,
      this.maxDevicePixelRatio,
    );
    const pixelWidth = Math.max(1, Math.round(width * devicePixelRatio));
    const pixelHeight = Math.max(1, Math.round(height * devicePixelRatio));
    if (this.canvas.width !== pixelWidth) this.canvas.width = pixelWidth;
    if (this.canvas.height !== pixelHeight) this.canvas.height = pixelHeight;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.width = width;
    this.height = height;
    this.devicePixelRatio = devicePixelRatio;
    return this.getStatus();
  }

  render(renderState, timestamp = undefined) {
    if (!this.started) throw new Error('CanvasWorldRenderer must be started before rendering');
    if (!this.visible) return this.getStatus();
    assertPlainObject(renderState, 'renderState');
    if (renderState.version !== RENDER_STATE_VERSION) {
      throw new RangeError(`Unsupported render-state version: ${renderState.version}`);
    }
    if (typeof renderState.checksum !== 'string') {
      throw new TypeError('renderState.checksum must be a string');
    }

    const startedAt = this.windowRef?.performance?.now?.() ?? Date.now();
    try {
      this.resize();
      const context = this.context;
      context.setTransform(this.devicePixelRatio, 0, 0, this.devicePixelRatio, 0, 0);
      context.clearRect(0, 0, this.width, this.height);
      this.#drawBackground(context, renderState);
      const projection = this.#createProjection(renderState.camera);
      if (renderState.diagnostics.showGrid || this.diagnostics.showGrid) {
        this.#drawGrid(context, renderState, projection);
      }
      this.#drawAmbientVegetation(context, renderState, projection);
      this.#drawVolumes(context, renderState, projection);
      this.#drawWildlife(context, renderState, projection);
      this.#drawSnake(context, renderState, projection);
      this.#drawHud(context, renderState);

      this.frameCount += 1;
      this.lastTick = renderState.tick;
      this.lastChecksum = renderState.checksum;
      this.lastCounts = {
        wildlife: renderState.wildlife.length,
        volumes: renderState.traversal.volumes.length,
        actors: renderState.traversal.actors.length,
      };
      this.lastError = null;
      const endedAt = this.windowRef?.performance?.now?.() ?? Date.now();
      this.lastRenderMilliseconds = Math.max(0, endedAt - startedAt);
      this.#updateOverlay(renderState, timestamp);
      return this.getStatus();
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : String(error);
      throw error;
    }
  }

  getCanvas() {
    return this.canvas;
  }

  getStatus() {
    return Object.freeze({
      version: CANVAS_WORLD_RENDERER_VERSION,
      started: this.started,
      visible: this.visible,
      frameCount: this.frameCount,
      lastTick: this.lastTick,
      lastChecksum: this.lastChecksum,
      lastRenderMilliseconds: this.lastRenderMilliseconds,
      lastError: this.lastError,
      width: this.width,
      height: this.height,
      devicePixelRatio: this.devicePixelRatio,
      wildlifeCount: this.lastCounts.wildlife,
      volumeCount: this.lastCounts.volumes,
      actorCount: this.lastCounts.actors,
      diagnostics: this.diagnostics,
    });
  }

  #createProjection(camera) {
    const scale = (Math.min(this.width, this.height) * 0.5)
      / (camera.viewRadius / camera.zoom);
    const forwardX = Math.sin(camera.heading);
    const forwardZ = Math.cos(camera.heading);
    const rightX = Math.cos(camera.heading);
    const rightZ = -Math.sin(camera.heading);
    return (position) => {
      const dx = position.x - camera.target.x;
      const dz = position.z - camera.target.z;
      const localX = (dx * rightX) + (dz * rightZ);
      const localForward = (dx * forwardX) + (dz * forwardZ);
      return {
        x: (this.width * 0.5) + (localX * scale),
        y: (this.height * 0.56) - (localForward * scale) - ((position.y - camera.target.y) * scale),
        scale,
      };
    };
  }

  #drawBackground(context, renderState) {
    const weatherKey = renderState.environment.weather.toLowerCase();
    const palette = WEATHER_PALETTE[weatherKey] ?? WEATHER_PALETTE.clear;
    const night = /night|dusk|midnight/.test(renderState.environment.timeOfDay.toLowerCase());
    const gradient = context.createLinearGradient(0, 0, 0, this.height);
    gradient.addColorStop(0, night ? '#071219' : palette.top);
    gradient.addColorStop(1, night ? '#020605' : palette.bottom);
    context.fillStyle = gradient;
    context.fillRect(0, 0, this.width, this.height);

    const glow = context.createRadialGradient(
      this.width * 0.5,
      this.height * 0.38,
      0,
      this.width * 0.5,
      this.height * 0.45,
      Math.max(this.width, this.height) * 0.72,
    );
    glow.addColorStop(0, night ? 'rgba(56, 96, 111, 0.08)' : 'rgba(104, 153, 112, 0.12)');
    glow.addColorStop(1, 'rgba(0, 0, 0, 0)');
    context.fillStyle = glow;
    context.fillRect(0, 0, this.width, this.height);

    context.fillStyle = palette.mist;
    for (let index = 0; index < 7; index += 1) {
      const y = this.height * (0.2 + (index * 0.11));
      context.fillRect(0, y, this.width, Math.max(1, this.height * 0.025));
    }
  }

  #drawGrid(context, renderState, project) {
    const radius = renderState.camera.viewRadius / renderState.camera.zoom;
    const center = renderState.camera.target;
    const startX = Math.floor(center.x - radius) - 1;
    const endX = Math.ceil(center.x + radius) + 1;
    const startZ = Math.floor(center.z - radius) - 1;
    const endZ = Math.ceil(center.z + radius) + 1;
    context.save();
    context.strokeStyle = 'rgba(169, 209, 179, 0.07)';
    context.lineWidth = 1;
    context.beginPath();
    for (let x = startX; x <= endX; x += 1) {
      const from = project({ x, y: 0, z: startZ });
      const to = project({ x, y: 0, z: endZ });
      context.moveTo(from.x, from.y);
      context.lineTo(to.x, to.y);
    }
    for (let z = startZ; z <= endZ; z += 1) {
      const from = project({ x: startX, y: 0, z });
      const to = project({ x: endX, y: 0, z });
      context.moveTo(from.x, from.y);
      context.lineTo(to.x, to.y);
    }
    context.stroke();
    context.restore();
  }

  #drawAmbientVegetation(context, renderState, project) {
    const center = renderState.camera.target;
    context.save();
    context.strokeStyle = 'rgba(84, 139, 82, 0.34)';
    context.fillStyle = 'rgba(61, 107, 64, 0.2)';
    for (const point of this.ambientPoints) {
      const world = {
        x: point.x + (Math.round(center.x / 20) * 20),
        y: 0,
        z: point.z + (Math.round(center.z / 20) * 20),
      };
      const screen = project(world);
      if (screen.x < -20 || screen.x > this.width + 20 || screen.y < -20 || screen.y > this.height + 20) {
        continue;
      }
      const height = Math.max(2, point.height * screen.scale);
      context.beginPath();
      context.moveTo(screen.x, screen.y);
      context.lineTo(screen.x + Math.sin(point.x * 3) * height * 0.2, screen.y - height);
      context.stroke();
      context.beginPath();
      context.arc(screen.x, screen.y, Math.max(0.6, point.width * screen.scale), 0, Math.PI * 2);
      context.fill();
    }
    context.restore();
  }

  #drawVolumes(context, renderState, project) {
    const diagnostics = {
      showTraversal: renderState.diagnostics.showTraversal || this.diagnostics.showTraversal,
      showLabels: renderState.diagnostics.showLabels || this.diagnostics.showLabels,
      showBounds: renderState.diagnostics.showBounds || this.diagnostics.showBounds,
    };
    for (const volume of renderState.traversal.volumes) {
      const palette = VOLUME_PALETTE[volume.kind] ?? VOLUME_PALETTE.solid;
      const corners = [
        project({ x: volume.bounds.min.x, y: volume.bounds.min.y, z: volume.bounds.min.z }),
        project({ x: volume.bounds.max.x, y: volume.bounds.min.y, z: volume.bounds.min.z }),
        project({ x: volume.bounds.max.x, y: volume.bounds.min.y, z: volume.bounds.max.z }),
        project({ x: volume.bounds.min.x, y: volume.bounds.min.y, z: volume.bounds.max.z }),
      ];
      context.save();
      context.beginPath();
      context.moveTo(corners[0].x, corners[0].y);
      for (let index = 1; index < corners.length; index += 1) {
        context.lineTo(corners[index].x, corners[index].y);
      }
      context.closePath();
      context.fillStyle = palette.fill;
      context.fill();
      if (diagnostics.showTraversal || diagnostics.showBounds) {
        context.strokeStyle = palette.stroke;
        context.lineWidth = diagnostics.showBounds ? 2 : 1;
        context.stroke();
      }
      if (diagnostics.showLabels) {
        const centerX = corners.reduce((sum, point) => sum + point.x, 0) / corners.length;
        const centerY = corners.reduce((sum, point) => sum + point.y, 0) / corners.length;
        context.fillStyle = 'rgba(239, 255, 243, 0.92)';
        context.font = '11px ui-monospace, monospace';
        context.textAlign = 'center';
        context.fillText(`${volume.kind} · ${volume.id}`, centerX, centerY);
      }
      context.restore();
    }
  }

  #drawWildlife(context, renderState, project) {
    const showLabels = renderState.diagnostics.showLabels || this.diagnostics.showLabels;
    for (const animal of renderState.wildlife) {
      const point = project(animal.position);
      if (point.x < -60 || point.x > this.width + 60 || point.y < -60 || point.y > this.height + 60) {
        continue;
      }
      const rotation = animal.heading - renderState.camera.heading;
      const base = clamp(point.scale * 0.12, 3, 22);
      context.save();
      context.translate(point.x, point.y);
      context.rotate(rotation);
      context.globalAlpha = animal.lifecycle === 'corpse' ? 0.5 : 1;
      this.#drawWildlifeShape(context, animal, base);
      context.restore();

      if (showLabels) {
        context.save();
        context.fillStyle = 'rgba(238, 250, 241, 0.9)';
        context.font = '11px ui-monospace, monospace';
        context.textAlign = 'center';
        context.fillText(
          `${animal.speciesId} · ${animal.behavior}`,
          point.x,
          point.y - base - 8,
        );
        context.restore();
      }
    }
  }

  #drawWildlifeShape(context, animal, base) {
    if (animal.speciesId === 'marsh-insect') {
      context.fillStyle = animal.lifecycle === 'corpse' ? '#625f55' : '#d8d35f';
      context.beginPath();
      context.arc(0, 0, Math.max(2, base * 0.28), 0, Math.PI * 2);
      context.fill();
      context.strokeStyle = 'rgba(245, 240, 150, 0.65)';
      context.beginPath();
      context.moveTo(-base * 0.8, 0);
      context.lineTo(base * 0.8, 0);
      context.stroke();
      return;
    }

    if (animal.speciesId === 'mud-minnow') {
      context.fillStyle = animal.lifecycle === 'corpse' ? '#5e625f' : '#78b9ae';
      context.beginPath();
      context.ellipse(0, 0, base * 0.8, base * 0.35, 0, 0, Math.PI * 2);
      context.fill();
      context.beginPath();
      context.moveTo(0, -base * 0.18);
      context.lineTo(-base, -base * 0.55);
      context.lineTo(-base, base * 0.55);
      context.closePath();
      context.fill();
      return;
    }

    if (animal.speciesId === 'great-blue-heron') {
      context.fillStyle = animal.lifecycle === 'corpse' ? '#666a68' : '#a9b7b6';
      context.beginPath();
      context.ellipse(0, 0, base * 0.55, base, 0, 0, Math.PI * 2);
      context.fill();
      context.strokeStyle = '#d0dcda';
      context.lineWidth = Math.max(1, base * 0.16);
      context.beginPath();
      context.moveTo(0, -base * 0.6);
      context.lineTo(0, -base * 1.7);
      context.stroke();
      context.fillStyle = '#d6a550';
      context.beginPath();
      context.moveTo(0, -base * 1.9);
      context.lineTo(-base * 0.22, -base * 1.55);
      context.lineTo(base * 0.22, -base * 1.55);
      context.closePath();
      context.fill();
      return;
    }

    context.fillStyle = animal.lifecycle === 'corpse' ? '#5d5b53' : '#668d3f';
    context.beginPath();
    context.ellipse(0, 0, base, base * 0.72, 0, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = '#d7e49b';
    context.beginPath();
    context.arc(-base * 0.45, -base * 0.45, base * 0.18, 0, Math.PI * 2);
    context.arc(base * 0.45, -base * 0.45, base * 0.18, 0, Math.PI * 2);
    context.fill();
  }

  #drawSnake(context, renderState, project) {
    const snake = renderState.snake;
    if (!snake) {
      context.save();
      context.fillStyle = 'rgba(229, 243, 234, 0.72)';
      context.font = '600 16px ui-sans-serif, system-ui';
      context.textAlign = 'center';
      context.fillText('Spawn a snake through the Test Lab or automation API.', this.width * 0.5, this.height * 0.55);
      context.restore();
      return;
    }
    const points = snake.bodySegments.map(project);
    const baseRadius = clamp(points[0].scale * (0.075 + (snake.mass * 0.08)), 3.5, 24);
    context.save();
    context.globalAlpha = snake.traversal.hidden ? 0.48 : snake.lifecycle === 'dead' ? 0.5 : 1;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = snake.traversal.mode === 'water' ? '#5d9f78' : '#77994f';
    context.lineWidth = baseRadius * 1.55;
    context.beginPath();
    const tail = points[points.length - 1];
    context.moveTo(tail.x, tail.y);
    for (let index = points.length - 2; index >= 0; index -= 1) {
      context.lineTo(points[index].x, points[index].y);
    }
    context.stroke();

    for (let index = points.length - 1; index >= 0; index -= 1) {
      const progress = 1 - (index / Math.max(1, points.length - 1));
      const radius = baseRadius * (0.45 + (progress * 0.55));
      context.fillStyle = index % 2 === 0 ? '#789b50' : '#668944';
      context.beginPath();
      context.arc(points[index].x, points[index].y, radius, 0, Math.PI * 2);
      context.fill();
    }

    const head = points[0];
    const rotation = snake.heading - renderState.camera.heading;
    context.translate(head.x, head.y);
    context.rotate(rotation);
    context.fillStyle = '#83a95b';
    context.beginPath();
    context.ellipse(0, -baseRadius * 0.12, baseRadius * 0.95, baseRadius * 1.2, 0, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = '#0d140d';
    context.beginPath();
    context.arc(-baseRadius * 0.42, -baseRadius * 0.62, Math.max(1.1, baseRadius * 0.12), 0, Math.PI * 2);
    context.arc(baseRadius * 0.42, -baseRadius * 0.62, Math.max(1.1, baseRadius * 0.12), 0, Math.PI * 2);
    context.fill();
    context.restore();

    const showLabels = renderState.diagnostics.showLabels || this.diagnostics.showLabels;
    if (showLabels) {
      context.save();
      context.fillStyle = 'rgba(238, 250, 241, 0.94)';
      context.font = '11px ui-monospace, monospace';
      context.textAlign = 'center';
      context.fillText(
        `${snake.species} · ${snake.traversal.mode}${snake.traversal.hidden ? ' · hidden' : ''}`,
        head.x,
        head.y - baseRadius - 10,
      );
      context.restore();
    }
  }

  #drawHud(context, renderState) {
    context.save();
    context.fillStyle = 'rgba(3, 9, 6, 0.68)';
    context.fillRect(14, 14, Math.min(360, this.width - 28), renderState.snake ? 112 : 76);
    context.fillStyle = '#e6f3e9';
    context.font = '700 14px ui-sans-serif, system-ui';
    context.textAlign = 'left';
    context.fillText('ENGINE 2.0 WORLD', 26, 36);
    context.font = '12px ui-monospace, monospace';
    context.fillStyle = '#a7c6af';
    context.fillText(
      `Tick ${renderState.tick ?? '—'} · ${renderState.environment.timeOfDay} · ${renderState.environment.weather}`,
      26,
      56,
    );
    const mission = renderState.story.activeMissionId ?? renderState.story.mode;
    context.fillText(`Story: ${mission}`, 26, 74);
    if (renderState.snake) {
      const vitals = renderState.snake.vitals;
      context.fillStyle = '#d6eadb';
      context.fillText(
        `HP ${Math.round(vitals.health)} · Hunger ${Math.round(vitals.hunger)} · Stamina ${Math.round(vitals.stamina)}`,
        26,
        94,
      );
      context.fillText(
        `${renderState.snake.growth.stage} · ${renderState.snake.length.toFixed(2)}m · ${renderState.snake.traversal.mode}`,
        26,
        112,
      );
    }
    context.restore();
  }

  #updateOverlay(renderState, timestamp) {
    if (!this.overlay) return;
    this.overlay.dataset.tick = renderState.tick === null ? '' : String(renderState.tick);
    this.overlay.dataset.checksum = renderState.checksum;
    this.overlay.dataset.wildlifeCount = String(renderState.wildlife.length);
    this.overlay.dataset.volumeCount = String(renderState.traversal.volumes.length);
    this.overlay.textContent = `Engine 2.0 · ${renderState.wildlife.length} wildlife · ${renderState.traversal.volumes.length} volumes`;
    if (Number.isFinite(timestamp)) this.overlay.dataset.timestamp = String(timestamp);
  }
}
